import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Form,
  ListGroup,
  InputGroup,
  Container,
} from "react-bootstrap";
import { converse } from "../../api";
import { FaTwitter, FaCheck, FaMagic } from "react-icons/fa";
import { Copy, Send } from "react-feather";


export default function ChatAssistant() {
  const [messages, setMessages] = useState([]);
  const [hasStarted, setHasStarted] = useState(false);
  const [input, setInput] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null); 

  const examples = [
    "Give me a CFO-style financial health analysis",
    "Suggest ways to grow my brand based on recent sales",
    "Show trends in my customer retention over time",
    "Analyze my profit margins and give improvement advice",
    "What products are underperforming this quarter?",
    "Show me my sales summary this week",
    "List my top customers this month",
    "Compare last month’s and this month’s revenue",
    "Generate a report of my best-selling products",
    "Summarize my expenses for the quarter",
  ];

  const [currentExample, setCurrentExample] = useState(examples[0]);

  // 🔁 Rotate dynamic examples
  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % examples.length;
      setCurrentExample(examples[index]);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  // 💬 7 welcoming messages
  const greetings = [
    "What should we explore today?",
    "Ready to uncover new insights?",
    "Where do we start optimizing?",
    "Let’s find opportunities together.",
    "Which area needs clarity today?",
    "Ready when you are, let’s analyze.",
    "How can I help you grow?",
  ];


  // 🎲 Pick a random greeting on page load
  const [greeting, setGreeting] = useState("");
  useEffect(() => {
    const random = greetings[Math.floor(Math.random() * greetings.length)];
    setGreeting(random);
  }, []); // run only once

  const addMessage = (m) => setMessages((prev) => [...prev, m]);

  // ✅ Scroll to latest bot reply
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.from === "bot") {
        const container = document.querySelector(".chat-messages");
        if (container) {
          container.scrollTo({
            top: container.scrollHeight * 0.9,
            behavior: "smooth",
          });
        }
      }
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userText = input.trim();

    if (!hasStarted) {
      setHasStarted(true);
      addMessage({ from: "bot", text: "", isIntro: true });
    }

    addMessage({ from: "user", text: userText });
    setInput("");

    // 👇 Blur input after send
    if (inputRef.current) {
      inputRef.current.blur();
    }

    addMessage({ from: "bot", text: "..." });

    try {
      const res = await converse(userText);
      setMessages((prev) => prev.filter((m) => m.text !== "..."));

      if (res.result && typeof res.result === "object") {
        const { summary, advice, detail, table } = res.result;
        const html = `
          <div class="intent-block">
            ${summary ? `<div class="intent-summary mb-2">${summary}</div>` : ""}
            ${detail ? `<div class="intent-detail mb-2">${detail}</div>` : ""}
            ${advice ? `<div class="intent-advice">${advice}</div>` : ""}
            ${table ? `<div class="intent-table mt-2">${renderTableHTML(table)}</div>` : ""}
          </div>
        `;
        addMessage({ from: "bot", html });
        return;
      }

      if (typeof res === "string") {
        addMessage({ from: "bot", text: res });
        return;
      }

      addMessage({ from: "bot", text: "⚠️ Unexpected response format." });
    } catch (err) {
      // 🧹 Remove any "..." loading messages first
      setMessages((prev) => prev.filter((m) => m.text !== "..."));

      // 🧩 Add single, clear error message bubble
      addMessage({
        from: "bot",
        html: `<span>⚠️ Something went wrong.<br><a href="#" onclick="window.location.reload()">Check your network and refresh.</a></span>`,
      });

      //console.error(err);
    }

  };


  const renderTableHTML = (tableObj) => {
    if (Array.isArray(tableObj) && tableObj.length > 0) {
      const headers = Object.keys(tableObj[0]);
      const rows = tableObj
        .map(
          (row) =>
            `<tr>${headers.map((h) => `<td>${row[h] ?? ""}</td>`).join("")}</tr>`
        )
        .join("");
      return `
        <table class="table table-striped table-bordered table-sm mb-0">
          <thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      `;
    }
    return "";
  };

  const handleCopy = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000); // Reset after 2s
    } catch (err) {
      //console.error("Copy failed:", err);
    }
  };

  const stripHTML = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  return (
    <section className="container mtopcon">
      <div className="row">
        <div className="col-12">
          {!hasStarted ? (
            // 🟢 INITIAL LANDING SCREEN
            <div className="d-flex flex-column justify-content-center align-items-center vh-70 text-center">
              <span className="badge-retarget ml-2 font8 mb-3">
                Mæven — your AI business analyst
              </span>
              <h4 className="text-muted mb-1 font-bold">{greeting}</h4>
              <p className="text-secondary mb-4 font8">
                <FaMagic/> <em>{currentExample}</em>
              </p>

              <Form
                onSubmit={handleSend}
                className="w-100 d-flex justify-content-center"
                style={{ maxWidth: "600px" }}
              >
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Ask anything..."
                    className="rounded-pill px-3 py-2 text-center"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    autoFocus
                  />
                  <Button
                    type="submit"
                    className="rounded-pill px-4 ms-2"
                    disabled={!input.trim()}
                  >
                    <Send size={18} />
                  </Button>
                </InputGroup>
              </Form>
            </div>
          ) : (
            // 💬 MAIN CHAT INTERFACE
            <Container
              fluid
              className="chatgpt-container container d-flex flex-column bg-white pbchat"
            >
              <div
                className="chat-messages font9 flex-grow-1 overflow-auto"
                style={{ scrollBehavior: "smooth" }}
              >
                <ListGroup variant="flush">
                  {messages.map((m, i) => (
                    <ListGroup.Item
                      key={i}
                      className={`relative w-auto border-0 py-2 position-relative ${
                        m.from === "bot"
                          ? "bg-light bot-msg"
                          : "mt-3 mb-3 user-msg text-end badge-retarget font9"
                      }`}
                    >
                      {m.from === "bot" && (
                        <>
                          <div className="d-flex align-items-start justify-content-between mb-1 pt-1">
                            <strong className="d-inline-flex align-items-center">
                              <FaTwitter className="me-1" />
                              {m.isIntro ? <span className="fw-semibold">Mæven</span> : null}
                            </strong>

                            {/* ✅ Copy Button (Show only on real responses) */}
                            {(m.from === "bot" && (m.html || (m.text && m.text.trim() !== "..." && !m.isIntro))) && (
                              <button
                                className="copy-btn btn btn-link p-0 ms-2"
                                onClick={() => handleCopy(stripHTML(m.html || m.text), i)}
                                style={{
                                  color: copiedIndex === i ? "#28a745" : "#6c757d",
                                  textDecoration: "none",
                                  marginTop: "-7px",
                                  fontSize: "0.9rem",
                                }}
                                title={copiedIndex === i ? "Copied!" : "Copy"}
                              >
                                {copiedIndex === i ? <FaCheck /> : <Copy width={15} />}
                              </button>
                            )}

                          </div>
                        </>
                      )}

                      <span
                        dangerouslySetInnerHTML={{
                          __html: (m.html || m.text || "")
                            .replace(/\n(?!\s*<)/g, "<br>")
                            .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                            .replace(/\*(.*?)\*/g, "<em>$1</em>"),
                        }}
                      />
                    </ListGroup.Item>
                  ))}

                  <div ref={messagesEndRef} />
                </ListGroup>
              </div>

              {/* 🟣 Chat Input Fixed at Bottom */}
              <div
                className="chat-input rounded-pill bg-inherit p-3"
                style={{
                  position: "fixed",
                  bottom: "-30px",
                  right: 0,
                  background: "white",
                  zIndex: 100,
                  boxShadow: "0 -2px 8px rgba(0,0,0,0.07)",
                }}
              >
                <Form onSubmit={handleSend}>
                  <InputGroup className="container">
                    <Form.Control
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Type your message..."
                      className="rounded-pill px-3"
                    />
                    <Button
                      type="submit"
                      className="rounded-pill px-4 ms-2"
                      disabled={!input.trim()}
                    >
                      <Send size={18} />
                    </Button>
                  </InputGroup>
                </Form>
              </div>


            </Container>
          )}
        </div>
      </div>
    </section>
  );
}
